import React from 'react';
import { Box, Card, CardContent, Flex, Heading, Text, TagBadge } from '@chatsdet/ui';
import { Database } from '@phosphor-icons/react';
import { DynamicResultRenderer, SqlDisplay, type DynamicResult } from './DynamicResultRenderer';
import { SaveToDashboardMenu } from './dashboards/SaveToDashboardMenu';
import type {
  ExecuteSqlOutput,
  PresentResultsOutput,
  PresentResultsInput,
  UserEventsUIMessage,
} from './types';

type ResultCardData = {
  id: string;
  sql: string | null;
  result: DynamicResult;
  rowCount: number | null;
  truncated: boolean;
  durationMs: number | null;
};

const getToolName = (part: { type?: string; toolName?: string }): string | null => {
  if (typeof part.toolName === 'string') return part.toolName;
  if (typeof part.type === 'string' && part.type.startsWith('tool-')) {
    return part.type.replace(/^tool-/, '');
  }
  return null;
};

/**
 * Walk all messages in order; for every present_results tool call with an available
 * output, pair it with the most recent preceding successful execute_sql output.
 */
function buildCards(messages: UserEventsUIMessage[]): ResultCardData[] {
  const cards: ResultCardData[] = [];
  let lastSql: { query: string | null; output: ExecuteSqlOutput | null } | null = null;

  for (const message of messages) {
    for (let i = 0; i < message.parts.length; i++) {
      const rawPart = message.parts[i] as {
        type?: string;
        toolName?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        toolCallId?: string;
      };
      const toolName = getToolName(rawPart);
      if (!toolName) continue;

      if (toolName === 'execute_sql') {
        const out = rawPart.output as ExecuteSqlOutput | undefined;
        const input = rawPart.input as { query?: string } | undefined;
        lastSql = {
          query: input?.query ?? out?.query ?? null,
          output: out ?? null,
        };
        continue;
      }

      if (toolName === 'present_results' && rawPart.state === 'output-available') {
        const presentation = (rawPart.output ?? rawPart.input) as
          | PresentResultsOutput
          | PresentResultsInput
          | undefined;
        if (!presentation) continue;

        const sql = lastSql?.query ?? null;
        const sqlOutput = lastSql?.output;
        const data =
          sqlOutput && sqlOutput.ok === true ? sqlOutput.data : ([] as Record<string, unknown>[]);

        cards.push({
          id: rawPart.toolCallId ?? `${message.id}-${i}`,
          sql,
          result: {
            renderType: presentation.renderType,
            title: presentation.title,
            xKey: presentation.xKey,
            yKey: presentation.yKey,
            nameKey: presentation.nameKey,
            data,
          },
          rowCount: sqlOutput && sqlOutput.ok === true ? sqlOutput.rowCount : null,
          truncated: !!(sqlOutput && sqlOutput.ok === true && sqlOutput.truncated),
          durationMs: sqlOutput && sqlOutput.ok === true ? sqlOutput.durationMs : null,
        });
      }
    }
  }

  return cards;
}

export function UserEventsCanvas({ messages }: { messages: UserEventsUIMessage[] }) {
  const cards = React.useMemo(() => buildCards(messages), [messages]);
  const orderedCards = React.useMemo(() => cards.slice().reverse(), [cards]);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
  }, [orderedCards.length]);

  if (cards.length === 0) {
    return (
      <Flex direction="column" align="center" justify="center" className="h-full text-center px-8">
        <Database size={48} className="text-muted-foreground/40 mb-4" />
        <Heading level={3} className="mb-2">
          User Events Explorer
        </Heading>
        <Text className="text-muted-foreground max-w-md">
          Ask questions in the chat panel and results will show up here as charts, tables, or big
          numbers.
        </Text>
      </Flex>
    );
  }

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto p-6">
      <Box className="space-y-6 max-w-5xl mx-auto">
        {orderedCards.map((card) => (
          <ResultCard key={card.id} card={card} />
        ))}
      </Box>
    </div>
  );
}

function ResultCard({ card }: { card: ResultCardData }) {
  return (
    <Box className="space-y-3">
      <Flex justify="between" align="center">
        <Heading level={3}>{card.result.title ?? 'Results'}</Heading>
        <Flex gap="sm" align="center">
          <TagBadge name={card.result.renderType.replace('_', ' ')} color="gray" hideDot />
          {card.rowCount !== null && (
            <Text size="xs" className="text-muted-foreground">
              {card.rowCount.toLocaleString()} row{card.rowCount === 1 ? '' : 's'}
              {card.truncated ? ' (truncated)' : ''}
            </Text>
          )}
          {card.durationMs !== null && (
            <Text size="xs" className="text-muted-foreground">
              {card.durationMs}ms
            </Text>
          )}
          {card.sql && (
            <SaveToDashboardMenu
              payload={{
                title: card.result.title ?? 'Untitled card',
                sql: card.sql,
                renderType: card.result.renderType,
                xKey: card.result.xKey ?? null,
                yKey: card.result.yKey ?? null,
                nameKey: card.result.nameKey ?? null,
              }}
            />
          )}
        </Flex>
      </Flex>

      {card.sql && <SqlDisplay sql={card.sql} />}

      {card.result.renderType === 'table' ? (
        <DynamicResultRenderer result={card.result} />
      ) : (
        <Card>
          <CardContent>
            <DynamicResultRenderer result={card.result} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
